import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext();

const BACKEND_MODE_KEY = "adminBackendMode";
const LOCAL_BASE_URL = import.meta.env.VITE_LOCAL_API_BASE_URL || "http://localhost:8080";
const LIVE_BASE_URL = import.meta.env.VITE_LIVE_API_BASE_URL || "https://server.equaljustice.ai";

const getBaseUrlFromMode = (mode) => (mode === "local" ? LOCAL_BASE_URL : LIVE_BASE_URL);
const getDefaultBackendMode = () =>
  window.location.hostname === "localhost" ? "local" : "live";

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("adminToken"));
  const [backendMode, setBackendMode] = useState(
    localStorage.getItem(BACKEND_MODE_KEY) || getDefaultBackendMode(),
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.defaults.baseURL = getBaseUrlFromMode(backendMode);
  }, [backendMode]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      verifyToken();
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async () => {
    try {
      const response = await axios.get("/api/admin/me");
      setUser(response.data.admin);
      setLoading(false);
    } catch (error) {
      console.error("Token verification failed:", error);
      logout();
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post("/api/admin/login", {
        email,
        password,
      });
      const { token, admin } = response.data;

      localStorage.setItem("adminToken", token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      setUser(admin);
      return { success: true };
    } catch (error) {
      console.error("Login failed:", error);
      return {
        success: false,
        error: error.response?.data?.message || "Login failed",
      };
    }
  };

  const signup = async (email, password) => {
    try {
      const response = await axios.post("/api/admin/signup", {
        email,
        password,
      });
      const { token, admin } = response.data;

      localStorage.setItem("adminToken", token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      setUser(admin);
      return { success: true };
    } catch (error) {
      console.error("Signup failed:", error);
      return {
        success: false,
        error: error.response?.data?.message || "Signup failed",
      };
    }
  };

  const logout = () => {
    localStorage.removeItem("adminToken");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
  };

  const switchBackend = (mode) => {
    const nextMode = mode === "live" ? "live" : "local";
    localStorage.setItem(BACKEND_MODE_KEY, nextMode);
    setBackendMode(nextMode);
    // Token validity is environment-specific; reset auth on backend switch.
    logout();
  };

  const value = {
    user,
    token,
    backendMode,
    backendBaseUrl: getBaseUrlFromMode(backendMode),
    loading,
    login,
    signup,
    logout,
    switchBackend,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
